/**
 * Super Admin → Site API seam: every call to `/api/super-admin/hq/site/*`
 * (docs/hq-site-module/hq-site-api-contract.md §5), and nothing else.
 *
 * Every method resolves to the response BODY, never the axios envelope — the same
 * `unwrap` shape as `lifecycle/api.js`. Relative imports (not "@/"): the alias is unmapped
 * under Jest, and every hook in this directory takes an injectable `api` (default: this
 * object) so a test can pass a double without touching axios.
 */
import { api } from "../../lib/api";

export const SITE_ADMIN_BASE = "/super-admin/hq/site";

const unwrap = (p) => p.then((r) => r.data);
const contentPath = (key) => `${SITE_ADMIN_BASE}/content/${encodeURIComponent(key)}`;
const mediaPath = (mediaId) => `${SITE_ADMIN_BASE}/media/${encodeURIComponent(mediaId)}`;
const linkPath = (linkId) => `${SITE_ADMIN_BASE}/links/${encodeURIComponent(linkId)}`;

export const siteApi = {
  /** → Schema */
  getSchema: () => unwrap(api.get(`${SITE_ADMIN_BASE}/schema`)),

  /** → { items: [Block] } — stored overrides only. */
  listContent: () => unwrap(api.get(`${SITE_ADMIN_BASE}/content`)),
  /** text/multiline field. `values` = { value_fr, value_en, value_es } ("" = no override). → Block */
  putText: (key, values) => unwrap(api.put(contentPath(key), values)),
  /** image field. → Block */
  putImage: (key, mediaId) => unwrap(api.put(contentPath(key), { media_id: mediaId })),
  /** Back to the default. → { ok: true } */
  resetContent: (key) => unwrap(api.delete(contentPath(key))),

  /** → { items: [Media] }, newest first. */
  listMedia: () => unwrap(api.get(`${SITE_ADMIN_BASE}/media`)),
  /** One image file, multipart field `file`. → Media */
  uploadMedia: (file) => {
    const form = new FormData();
    form.append("file", file);
    return unwrap(api.post(`${SITE_ADMIN_BASE}/media`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    }));
  },
  /** `patch` ⊆ { alt_fr, alt_en, alt_es }. → Media */
  updateMedia: (mediaId, patch) => unwrap(api.patch(mediaPath(mediaId), patch)),
  /** → { references: [Ref] } */
  mediaUsage: (mediaId) => unwrap(api.get(`${mediaPath(mediaId)}/usage`)),
  /** Soft delete; 409 MEDIA_IN_USE when referenced. → { ok: true } */
  deleteMedia: (mediaId) => unwrap(api.delete(mediaPath(mediaId))),

  /** → { items: [Link] }, sorted (location, group, order). */
  listLinks: () => unwrap(api.get(`${SITE_ADMIN_BASE}/links`)),
  /** `link` = LinkIn. → Link */
  createLink: (link) => unwrap(api.post(`${SITE_ADMIN_BASE}/links`, link)),
  /** Full replace. `link` = LinkIn. → Link */
  updateLink: (linkId, link) => unwrap(api.put(linkPath(linkId), link)),
  /** → { ok: true } */
  deleteLink: (linkId) => unwrap(api.delete(linkPath(linkId))),
  /** Rewrites the bucket's orders 0..n-1. → { items: [Link] } of that bucket. */
  reorderLinks: ({ location, group, linkIds }) =>
    unwrap(api.put(`${SITE_ADMIN_BASE}/links/reorder`, { location, group, link_ids: linkIds })),
};

export default siteApi;
